import { useMsal } from "@azure/msal-react";
import { FocusTrapZone, Layer, Overlay, Stack, mergeStyles, useTheme } from "@fluentui/react";
import { RouterParams } from "App";
import { RootState } from "app/store";
import { saveAs } from "file-saver";
import { CSSProperties, useEffect, useState } from "react";
import { ConnectedProps, connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from "react-router";
import { AppLinkTo, EViewType, createDefaultCategoryFields, emitCustomEvent, slideRightIn400 } from "./Application";
import { appInsights } from "./Application/AppInsight";
import { loadSnapshotAction } from "./Application/dataActions";
import { useAuthentication } from "./Authentication";
import { AppDataSource } from "./DataSources/AppDataSource";
import { MsalTokenSource } from "./DataSources/MsalTokenSource";
import { reconcileCESNumbers, updateSnapshotData } from "./Reducer/dataSlice";
import { setProcessing, setViewType, updateDebug } from './Reducer/statusSlice';
import { EJsonTransformerRuntime, JsonTransformer } from "./Transform/JsonTransformer";
import { RemoveDeprecatedFields } from "./Transform/RemoveDeprecatedFields";
import { IGLWindow } from "model/GroupLibraryWindow";
import { TCategoryName } from "model/CRM.type";

declare global {
    interface Array<T> {
        insert(index: number, elem: T[]): void;
        remove(elem: T): Array<T>;
    }
}


export interface IDebugOptions {
    admin?: boolean;
    diagnosis?: boolean;
    exception?: boolean;
    clone?: boolean;
    disableAutosave?: boolean;
    autoSaveOff?: boolean;
    ignoreVersion?: boolean;
    liveload?: boolean;
    noVisibility?: boolean;
    checklistForceRecycle?: boolean;
    developer?: boolean;
    quickLoad?: boolean;
    preview?: boolean;
};



const overlay: CSSProperties = {
    height: '100%',
    width: '100%',
    position: 'fixed',
    backgroundColor: 'rgba(0,0,0, 0.9)',
    left: '0',
    top: '0',
    overflowX: 'hidden',
    transition: '0.6s',
    zIndex: 100000,

}

const overlayContent: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    minHeight: '100%',
    width: '100%',
    transition: '0.6s',
}

const anchorClass = mergeStyles({
    padding: '8px',
    textDecoration: 'none',
    fontSize: '32px',
    color: '#818181',
    display: 'block',
    transition: '0.3s',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    ":hover": {
        color: '#f1f1f1',
    }
});

export interface IDebuggerProps extends TComponentConnectedProps {

}

enum IDebuggerState {
    unknown,
    administrative,
    developer,
}

const Debugger: React.FC<IDebuggerProps> = (props) => {

    const { bcbs_emchecklistid } = useParams<RouterParams>();

    const location = useLocation();

    const isStandAlone = location.pathname === '/debug';

    const [hidden, setHidden] = useState(true);
    const authentication = useAuthentication();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { instance } = useMsal();

    const state = useSelector((state: RootState) => state);

    const [viewModel, setViewModel] = useState(
        {
            mode: IDebuggerState.unknown
        }
    );

    const history = useHistory();

    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    useEffect(() => {
        if (isStandAlone)
            setHidden(false);
    }, [])


    useEffect(() => {

        const debuggerOnKeyUp = (event: KeyboardEvent) => {
            if (event.code === 'Backquote') {
                setHidden(t => !t);

            } else if (event.code === 'Escape') {
                setHidden(true)
            }
        }

        document.addEventListener('keyup', debuggerOnKeyUp);

        return () => {
            document.removeEventListener('keyup', debuggerOnKeyUp)
        }
    }, []);

    const debugRemoveDeprecatedFields = async () => {

        const snapshot = state.data.snapshot;
        const jsonNew = structuredClone(state.data.json);

        const transformer = new JsonTransformer(
            [
                new RemoveDeprecatedFields({
                    config: state.data.config,
                    json: jsonNew
                })
            ],
            EJsonTransformerRuntime.ESequential
        );

        await transformer.transformAsync(jsonNew, snapshot);

        dispatch(updateDebug({ autoSaveOff: true }));
        dispatch(updateSnapshotData({ json: jsonNew }));

    }

    const debugExportNS = async () => {

    }

    const debugDumpJsonAsKeyValue = async () => {
        const json = structuredClone(state.data.json);

        const log: Record<string, string> = {};

        const print = (object: any, context: string[], scope: string) => {

            const keys = Object.keys(object);
            const lContext = [...context, scope];

            keys.forEach((eachKey: any) => {
                const eachObject = object[eachKey];

                if (eachObject instanceof Object) {

                    if (lContext[1] === 'fields' && lContext.length === 2) {
                        eachKey = `${eachObject?.configRef?.id}`
                    }

                    if (lContext[1] === 'benefits' && lContext.length === 2) {
                        eachKey = `${eachObject.name}`
                    }


                    if (lContext[1] === 'plans' && lContext.length === 2) {
                        eachKey = `${eachObject.name}`
                    }

                    print(eachObject, lContext, eachKey);
                } else {
                    const value = eachObject;
                    const otherContext = [...lContext, eachKey];
                    log[otherContext.join(".")] = value;
                }
            });

        }
        print(json, [], '$');
        const blob = new Blob([JSON.stringify(log)], { type: 'text/plan;charset=utf-8' });
        saveAs(blob, `test.txt`, { autoBom: true });
    }

    const debugNestedValueFix = async () => {
        const importAwait = await import("global/Transform/Sprint22Migration")
        const jsonNew = structuredClone(state.data.json);
        const results = importAwait.Run(jsonNew);
        console.log(results)
        dispatch(updateDebug({ autoSaveOff: true }));
        dispatch(updateSnapshotData({ json: jsonNew }));
    }

    const debugNestedValueFixBatch = async () => {
        const importAwait = await import("global/Transform/Sprint22Migration")
        const results = importAwait.RunBatch(dataSource);
    }


    const onRenderScreenWrap = (child: JSX.Element) => {
        if (hidden)
            return <></>;

        return <Layer eventBubblingEnabled>
            <Overlay styles={{ root: { background: theme.semanticColors.bodyBackground } }}>
            </Overlay>
            <FocusTrapZone style={{ position: 'absolute', height: '100%', width: '100%' }} forceFocusInsideTrap={true}>
                <Stack styles={{ root: { padding: '15px', height: '100%', width: '100%' } }}>
                    {/* <Stack.Item align="end" styles={{ root: { marginBottom: '10px' } }}>
                        <IconButton
                            iconProps={{ iconName: "ChromeClose" }}
                            onClick={(event) => {
                            }}
                        />
                    </Stack.Item> */}
                    <Stack.Item styles={{ root: { height: '100%', width: '100%' } }}>
                        {child}
                    </Stack.Item>
                </Stack>
            </FocusTrapZone>
        </Layer>
    }

    const onRenderDeveloper = () => {

        const view = <button
            className={anchorClass}
            onClick={() => {
                setViewModel(vm => { return { ...vm, mode: IDebuggerState.developer } })
            }}
            style={{ color: viewModel.mode === IDebuggerState.developer ? 'white' : undefined }}
        >
            Developer
        </button>

        if (viewModel.mode !== IDebuggerState.developer)
            return view;

        return <>
            {view}
            <div className={slideRightIn400} style={{ paddingLeft: '25px' }}>
                <input type="file" id="browse" name="fileupload" style={{ display: 'none' }} />

                <button
                    className={anchorClass}
                    onClick={() => {
                        let domain = new URL(window.location.href);
                        domain.host = `localhost:44350`;
                        window.open(domain.toString());
                    }} >
                    🌐 Open in localhost
                </button>

                <button
                    className={anchorClass}
                    onClick={() => {

                        if (state.data.opportunityProduct) {
                            const log = JSON.stringify(state.data.opportunityProduct);
                            const blob = new Blob([log], { type: 'text/plan;charset=utf-8' });
                            saveAs(blob, `${state.data.snapshot.bcbs_name}.json`, { autoBom: true });

                        } else {
                            const log = JSON.stringify(state.data.json);
                            const blob = new Blob([log], { type: 'text/plan;charset=utf-8' });
                            saveAs(blob, `${state.data.snapshot.bcbs_name}.json`, { autoBom: true });
                        }

                    }} >
                    🗄️ Download store JSON
                </button>

                <button
                    className={anchorClass}
                    onClick={async () => {
                        const result = prompt('Enter Snapshot id', props?.state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid);

                        if (!result)
                            return;

                        const snapshot = await dataSource.getGroupConfigurationSnapshot(result);

                        if (snapshot) {
                            let result = await dataSource.downloadGroupConfigurationSnapshot(snapshot);

                            if (result?.json) {
                                const jsonStringify = JSON.stringify(result?.json);
                                const blob = new Blob([jsonStringify], { type: 'text/plan;charset=utf-8' });
                                saveAs(blob, `${snapshot.bcbs_name}.json`, { autoBom: true });
                            }
                        }
                    }}>
                    🗄️ Download Blob JSON
                </button>

                <button
                    className={anchorClass}
                    onClick={() => {
                        const log = JSON.stringify(state.data.config);
                        const blob = new Blob([log], { type: 'text/plan;charset=utf-8' });
                        saveAs(blob, 'config.json', { autoBom: true });
                    }} >
                    🗄️ Download release/config JSON
                </button>

                <button
                    className={anchorClass}
                    onClick={() => {
                        const fileInput = document.getElementById("browse")! as HTMLInputElement;
                        const handler = (event: any) => {
                            fileInput.removeEventListener('change', handler)
                            // add first image, if available
                            if (fileInput.files?.length) {
                                const x = fileInput.files[0];
                                const reader = new FileReader();
                                reader.onload = function (e: any) {
                                    const json = reader.result as string;
                                    const data = JSON.parse(json);
                                    dispatch(updateSnapshotData({ ...state.data, json: data }));
                                }
                                reader.readAsText(x);
                            };

                        }

                        fileInput.addEventListener('change', handler);
                        fileInput.click();
                    }} >
                    ♻️ replace JSON
                </button>

                {authentication.authorizations?.roles["BCBS - Power User"] &&
                    <button
                        className={anchorClass}
                        onClick={() => { history.push(AppLinkTo(props.state, { type: 'timeline', id: '' })) }} >
                        ⌚ Timeline
                    </button>
                }

                <button
                    className={anchorClass}
                    onClick={
                        () => {
                            dispatch(updateDebug({ ignoreVersion: !state.status.debug.ignoreVersion }));
                        }} >
                    {state.status.debug.ignoreVersion ? "🟢" : "⚫"} Ignore Version Number
                </button>


                <button
                    className={anchorClass}
                    onClick={() => { appInsights?.flush(true); }} >
                    👣 Insight Flush
                </button>

                <button
                    className={anchorClass}
                    onClick={() => { dispatch(updateDebug({ exception: !state.status.debug.exception })); }} >
                    👣 Simulate exception - process
                </button>

                <button
                    className={anchorClass}
                    onClick={() => {
                        setTimeout(() => {
                            throw new Error('Testing Global Scope Exception');
                        }, 4000);
                    }} >
                    👣 Simulate exception - global scope
                </button>

                <button
                    className={anchorClass} onClick={() => {
                        debugRemoveDeprecatedFields();
                    }}>
                    👣 Remove Deprecated Fields
                </button>

                <button
                    className={anchorClass}
                    onClick={() => {
                        dispatch(reconcileCESNumbers())
                    }}>
                    👣 Reconcile CES Numbers
                </button>

                <button
                    className={anchorClass}
                    onClick={() => {
                        debugNestedValueFix();
                    }}>
                    👣 Fix nested values
                </button>

                <button
                    className={anchorClass}
                    onClick={() => {
                        debugExportNS();
                    }}>
                    👣 Export Nonstandard
                </button>

                <button
                    className={anchorClass}
                    onClick={() => {
                        const x = 'Non-Standard Template' as any as TCategoryName;
                        const category = state.data.config.cache.map_bcbs_config_category_named[x];
                        debugger;
                        const test = createDefaultCategoryFields(state.data.config, category, '', []);

                    }}>
                    👣 Test
                </button>

            </div>
        </>
    }

    const onRenderAdministrative = () => {
        const view = <button
            className={anchorClass}
            style={{ color: viewModel.mode === IDebuggerState.administrative ? 'white' : undefined }}

            onClick={() => { setViewModel(vm => { return { ...vm, mode: IDebuggerState.administrative } }) }}
        >
            Administrative
        </button>

        if (viewModel.mode !== IDebuggerState.administrative)
            return view;

        return <>
            {view}
            <div className={slideRightIn400} style={{ paddingLeft: '25px' }}>
                <button
                    className={anchorClass}
                    onClick={() => {
                        dispatch(updateDebug({ liveload: true }));
                        dispatch(updateDebug({ autoSaveOff: true }));
                        dispatch(loadSnapshotAction(
                            dataSource,
                            state.data.account?.accountid!,
                            {
                                primarySnapshotId: state.data.snapshot.bcbs_groupconfigurationsnapshotid,
                            }
                        ));
                    }}
                >
                    {state.status.debug.liveload ? "🟢" : "⚫"} Reload with live configuration
                </button>


                <button
                    className={anchorClass}
                    onClick={() => {
                        dispatch(updateDebug({ noVisibility: true }));
                        dispatch(updateDebug({ autoSaveOff: true }));
                        dispatch(loadSnapshotAction(
                            dataSource,
                            state.data.account?.accountid!,
                            {
                                primarySnapshotId: state.data.snapshot.bcbs_groupconfigurationsnapshotid,
                            }
                        ));
                    }}
                >
                    {state.status.debug.noVisibility ? "🟢" : "⚫"} Reload without visibility transformers
                </button>

                <button className={anchorClass} onClick={
                    () => {
                        dispatch(updateDebug({ autoSaveOff: true }));

                        if (state.status.viewType === EViewType.ViewOnly)
                            dispatch(setViewType(EViewType.Modify));
                        else
                            dispatch(setViewType(EViewType.ViewOnly));

                    }} >
                    {state.status.viewType === EViewType.ViewOnly && <>✏️ Switch to Edit</>}
                    {state.status.viewType === EViewType.Modify && <>👁️ Switch to View</>}
                </button>

                <button className={anchorClass} onClick={
                    () => {
                        dispatch(updateDebug({ diagnosis: !state.status.debug.diagnosis }));
                        dispatch(updateDebug({ autoSaveOff: true }));
                    }} >
                    {state.status.debug.diagnosis ? "🟢" : "⚫"} Diagnosis
                </button>

                <button className={anchorClass} onClick={
                    () => {
                        dispatch(updateDebug({ admin: !state.status.debug.admin }));
                        dispatch(updateDebug({ autoSaveOff: true }));
                    }} >
                    {state.status.debug.admin ? "🟢" : "⚫"} Admin mode
                </button>

                <button className={anchorClass} onClick={
                    () => {
                        dispatch(updateDebug({ clone: !state.status.debug.clone }));
                        dispatch(updateDebug({ autoSaveOff: true }));
                    }} >
                    {state.status.debug.clone ? "🟢" : "⚫"} Clone on save
                </button>

                <button className={anchorClass} onClick={
                    () => {
                        dispatch(updateDebug({ autoSaveOff: !state.status.debug.autoSaveOff }));
                    }} >
                    {state.status.debug.autoSaveOff ? "🟢" : "⚫"} Autosave off
                </button>



                <button className={anchorClass} onClick={
                    () => {
                        if (document.cookie.split(';').some(q => q.indexOf('preview=yes') > -1)) {
                            document.cookie = "preview=;path=/";
                            (window as IGLWindow).preview = false;
                        } else {
                            document.cookie = "preview=yes;path=/";
                            (window as IGLWindow).preview = true;
                        }
                        dispatch(setProcessing(true));
                        window.location.reload();
                    }
                } >
                    {(window as IGLWindow).preview ? "🟢" : "⚫"} Preview
                </button>

                <button className={anchorClass} onClick={
                    () => {
                        (async () => {
                            try {

                                dispatch(setProcessing(true));
                                const response = await dataSource.getGenerateDataDictionaryExtract();
                                saveAs(response.blob, response.fileName);

                            } catch (error) {
                                // lets create a new one is payload is not providing one
                                emitCustomEvent({
                                    eventType: 'globalException',
                                    details: error
                                });

                            } finally {
                                dispatch(setProcessing(false));
                            }
                        })();
                    }} >
                    🌐 Download Data Model
                </button>
            </div >
        </>
    }

    const onRenderStandAlone = () => {

        return <>
            <div className={slideRightIn400} style={{ paddingLeft: '25px' }}>

            </div>
        </>
    }

    const onRenderView = () => {
        return (
            <div style={{ ...overlay, display: 'block``', overflowY: 'auto' }}>
                <div style={overlayContent}>
                    <h2>Management Terminal</h2>

                    {isStandAlone &&
                        onRenderStandAlone()
                    }

                    {!isStandAlone &&
                        <div>
                            {onRenderAdministrative()}
                            {onRenderDeveloper()}
                            <button className={anchorClass} onClick={() => { setHidden(true) }} >
                                Exit
                            </button>
                        </div>
                    }

                </div>
            </div>
        );
    }

    return onRenderScreenWrap(onRenderView());
}

// window.addEventListener('load', function () {
//     const debugDiv = document.createElement('div');
//     document.body.appendChild(debugDiv);
//     ReactDOM.render(
//       (<DebugView />),
//       debugDiv
//     );
//   })


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connect(mapStateToProps)(Debugger);